// 시장지수 현황 화면
import React, { useState, useEffect } from 'react';
import {
  ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import { fetchMarketIndex, collectVkospi } from '../../api/marketIndex';
import { fetchMultiPriceHistory } from '../../api/price';
import PageLayout from '../../components/PageLayout';
import {
  COMPARE_COLORS, normalizeSeries, marketFieldToSeries, mergeNormalized, CompareLineChart,
} from '../../components/common/CompareChart';
import './MarketIndexPage.css';

const toYMD = (d) => d.toISOString().slice(0, 10);
const calcDateRange = (days) => {
  const end = new Date(), start = new Date();
  start.setDate(end.getDate() - days);
  return { start: toYMD(start), end: toYMD(end) };
};
const fNum = (v) => {
  if (v == null) return '-';
  return Number(v).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fDiff = (v) => { if (v == null) return null; const n = Number(v); return (n > 0 ? '+' : '') + n.toFixed(2); };
const fRate = (v) => { if (v == null) return null; const n = Number(v); return (n > 0 ? '+' : '') + n.toFixed(2) + '%'; };
const diffColor = (v) => v == null ? '#888' : v > 0 ? '#e74c3c' : v < 0 ? '#4a90e2' : '#888';

const calcCorrelation = (xs, ys) => {
  const n = xs.length;
  if (n < 3) return NaN;
  const sx  = xs.reduce((a, b) => a + b, 0);
  const sy  = ys.reduce((a, b) => a + b, 0);
  const sxy = xs.reduce((a, b, i) => a + b * ys[i], 0);
  const sx2 = xs.reduce((a, b) => a + b * b, 0);
  const sy2 = ys.reduce((a, b) => a + b * b, 0);
  const denom = Math.sqrt((n * sx2 - sx ** 2) * (n * sy2 - sy ** 2));
  return denom === 0 ? NaN : (n * sxy - sx * sy) / denom;
};

const getVkospiColor = (value) => {
  if (value == null) return '#4444FF';
  const n = parseFloat(value);
  if (n >= 30) return '#FF4444';
  if (n >= 20) return '#FFA500';
  return '#4444FF';
};

const vkospiStatus = (v) => {
  if (v == null) return null;
  const n = parseFloat(v);
  if (n <= 20) return { emoji: '🟢', text: '안정',       color: '#2e7d32' };
  if (n <= 30) return { emoji: '🟡', text: '주의',       color: '#e65100' };
  if (n <= 40) return { emoji: '🔴', text: '공포',       color: '#c62828' };
  return           { emoji: '🚨', text: '극도의 공포', color: '#6a1b9a' };
};

const corrDescText = (r) => {
  if (isNaN(r)) return '';
  if (r >=  0.7) return '강한 양의 상관관계';
  if (r >=  0.3) return '보통 양의 상관관계';
  if (r >= -0.3) return '약한 상관관계';
  if (r >= -0.7) return '보통 음의 상관관계';
  return '강한 음의 상관관계';
};

const PERIODS = [
  { label: '1개월',   days: 30   },
  { label: '3개월',   days: 90   },
  { label: '6개월',   days: 180  },
  { label: '1년',     days: 365  },
  { label: '2년',     days: 730  },
  { label: '3년',     days: 1095 },
  { label: '5년',     days: 1825 },
  { label: '직접설정', days: null },
];

const TABS = [
  { id: 'domestic',    label: '국내지수'     },
  { id: 'us',          label: '미국지수'     },
  { id: 'forex',       label: '환율'         },
  { id: 'commodity',   label: '원자재'       },
  { id: 'correlation', label: '상관관계'     },
  { id: 'compare',     label: '2개종목비교'   },
];

// 탭별 차트 필드 (그리드와 별도 관리)
const TAB_CHART_FIELDS = {
  domestic: [
    { key: 'kospi',    label: '코스피'                  },
    { key: 'kosdaq',   label: '코스닥'                  },
    { key: 'vkospi',   label: 'VKOSPI (한국 공포지수)' },
    { key: 'kospi200', label: '코스피200'               },
  ],
  us: [
    { key: 'sp500',    label: 'S&P500'           },
    { key: 'nasdaq100',label: '나스닥100'         },
    { key: 'vix',      label: '미국 VIX 공포지수' },
    { key: 'dow',      label: '다우존스'          },
  ],
  forex: [
    { key: 'usd_krw', label: '원달러'     },
    { key: 'jpy_krw', label: '원엔'       },
    { key: 'dxy',     label: '달러인덱스' },
    { key: 'gold',    label: '금'         },
  ],
  commodity: [
    { key: 'gold',      label: '금'      },
    { key: 'silver',    label: '은'      },
    { key: 'copper',    label: '구리'    },
    { key: 'brent_oil', label: '브렌트유' },
  ],
};

// 탭별 그리드 필드
const TAB_GRID_FIELDS = {
  domestic: [
    { key: 'kospi',    label: '코스피'   },
    { key: 'kosdaq',   label: '코스닥'   },
    { key: 'kospi200', label: '코스피200' },
    { key: 'vkospi',   label: 'VKOSPI'  },
  ],
  us: [
    { key: 'sp500',    label: 'S&P500'           },
    { key: 'nasdaq100',label: '나스닥100'         },
    { key: 'dow',      label: '다우존스'          },
    { key: 'vix',      label: '미국 VIX 공포지수' },
  ],
  forex: [
    { key: 'usd_krw', label: '원달러'     },
    { key: 'dxy',     label: '달러인덱스' },
    { key: 'jpy_krw', label: '원엔'       },
    { key: 'usd_jpy', label: '달러엔'     },
  ],
  commodity: [
    { key: 'dxy',       label: '달러인덱스' },
    { key: 'gold',      label: '금'         },
    { key: 'silver',    label: '은'         },
    { key: 'copper',    label: '구리'       },
    { key: 'brent_oil', label: '브렌트유'   },
  ],
};

// 상관관계 탭 그리드 필드
const CORR_GRID_FIELDS = [
  { key: 'sp500',     label: 'S&P500'    },
  { key: 'vix',       label: '미국 VIX'  },
  { key: 'vkospi',    label: 'VKOSPI'   },
  { key: 'dxy',       label: '달러인덱스' },
  { key: 'gold',      label: '금'         },
  { key: 'usd_krw',   label: '원달러'     },
  { key: 'kospi',     label: '코스피'     },
  { key: 'brent_oil', label: '브렌트유'   },
];

// 상관관계 탭 2축 차트 설정
const CORR_CHARTS = [
  {
    id: 'sp500_vix',
    title: 'S&P500 vs 미국 VIX 공포지수',
    desc: '주가 급락 시 미국 VIX 급등 패턴 확인',
    leftKey: 'sp500',     leftLabel: 'S&P500',       leftColor: '#4a90e2',
    rightKey: 'vix',      rightLabel: '미국 VIX',    rightColor: '#e74c3c',
    leftInfoKey: null,    rightInfoKey: 'vix',
  },
  {
    id: 'dxy_gold',
    title: '달러인덱스 vs 금',
    desc: '달러 강세 시 금 가격 하락 경향 (역상관)',
    leftKey: 'dxy',  leftLabel: '달러인덱스', leftColor: '#4a90e2',
    rightKey: 'gold', rightLabel: '금',        rightColor: '#f5a623',
    leftInfoKey: 'dxy', rightInfoKey: null,
  },
  {
    id: 'usdkrw_kospi',
    title: '원달러 환율 vs 코스피',
    desc: '달러 강세(원화 약세) 시 외국인 매도 → 코스피 하락 경향',
    leftKey: 'usd_krw', leftLabel: '원달러', leftColor: '#4a90e2',
    rightKey: 'kospi',  rightLabel: '코스피', rightColor: '#e74c3c',
    leftInfoKey: null,  rightInfoKey: null,
  },
  {
    id: 'dxy_usdkrw',
    title: '달러인덱스 vs 원달러 환율',
    desc: '달러인덱스 상승(달러 강세) 시 원달러 환율 동반 상승 경향 (양상관)',
    leftKey: 'dxy',     leftLabel: '달러인덱스', leftColor: '#4a90e2',
    rightKey: 'usd_krw', rightLabel: '원달러 환율', rightColor: '#27ae60',
    leftInfoKey: 'dxy', rightInfoKey: null,
  },
];

const INFO_FIELD_KEYS = new Set(['vix', 'dxy', 'vkospi']);

const getLineColor = (chartData, key) => {
  const vals = chartData.filter(d => d[key] != null);
  if (vals.length < 2) return '#4a90e2';
  return vals[vals.length - 1][key] > vals[0][key] ? '#e74c3c' : '#4a90e2';
};

// ── 지수 설명 팝업 내용 ───────────────────────────────────────
const INFO_CONTENT = {
  vkospi: {
    title: '한국 VKOSPI 공포지수란?',
    body: (
      <div className="mi-info-body">
        <section className="mi-info-section">
          <h4 className="mi-info-h4">VKOSPI란?</h4>
          <p>VKOSPI(Volatility Index of KOSPI)는 한국거래소(KRX)가 산출하는 코스피200 옵션 기반 변동성 지수입니다. 향후 30일간 코스피200의 변동성을 예측하며, 수치가 높을수록 시장 불안감이 크다는 것을 의미합니다. 미국의 VIX 지수와 동일한 개념으로 한국 시장의 투자 심리를 파악하는 데 활용됩니다.</p>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">수치 해석 방법</h4>
          <table className="mi-info-table">
            <thead><tr><th>수치</th><th>시장 상태</th><th>의미</th></tr></thead>
            <tbody>
              <tr><td>20 이하</td><td>🟢 안정</td><td>시장 안정적, 투자자 낙관적</td></tr>
              <tr><td>20 ~ 30</td><td>🟡 주의</td><td>불확실성 증가, 투자자 불안</td></tr>
              <tr><td>30 ~ 40</td><td>🔴 공포</td><td>높은 공포, 시장 변동성 큼</td></tr>
              <tr><td>40 이상</td><td>🚨 극도의 공포</td><td>시장 패닉, 역사적 급락 수준</td></tr>
            </tbody>
          </table>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">코스피와의 관계</h4>
          <ul className="mi-info-ul">
            <li><strong>VKOSPI 급등 시</strong><br />→ 코스피 급락 신호, 투매 가능성<br />→ 단기 매수보다 추세 확인 후 진입 권장</li>
            <li><strong>VKOSPI 하락 시</strong><br />→ 시장 불안 해소, 코스피 반등 신호</li>
            <li><strong>VKOSPI와 코스피는 강한 역상관관계</strong><br />→ 코스피 하락 시 VKOSPI 급등, 코스피 상승 시 VKOSPI 하락</li>
          </ul>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">활용 팁</h4>
          <ul className="mi-info-ul">
            <li>✅ VKOSPI 급등 시 → 공포에 매수 전략 고려 (추세 확인 필수)</li>
            <li>✅ VKOSPI 낮을 때 → 리스크 관리 강화, 과열 주의</li>
            <li>✅ 코스피 차트와 함께 비교하면 더욱 효과적</li>
          </ul>
        </section>
      </div>
    ),
  },
  vix: {
    title: '미국 VIX 공포지수란?',
    body: (
      <div className="mi-info-body">
        <section className="mi-info-section">
          <h4 className="mi-info-h4">VIX 공포지수란?</h4>
          <p>VIX(Volatility Index)는 미국 시카고옵션거래소(CBOE)에서 발표하는 변동성 지수로, S&amp;P500 옵션 가격을 기반으로 향후 30일간 시장의 예상 변동성을 나타냅니다. '공포지수'라고도 불리며, 투자자들의 심리 상태를 수치로 표현한 지표입니다.</p>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">수치 해석 방법</h4>
          <table className="mi-info-table">
            <thead><tr><th>수치</th><th>시장 상태</th><th>의미</th></tr></thead>
            <tbody>
              <tr><td>0 ~ 15</td><td>🟢 안정</td><td>시장이 매우 안정적, 투자자 낙관적</td></tr>
              <tr><td>15 ~ 20</td><td>🟡 보통</td><td>정상적인 시장 변동성</td></tr>
              <tr><td>20 ~ 30</td><td>🟠 주의</td><td>불확실성 증가, 투자자 불안</td></tr>
              <tr><td>30 ~ 40</td><td>🔴 경계</td><td>높은 공포, 시장 변동성 큼</td></tr>
              <tr><td>40 이상</td><td>🚨 극도의 공포</td><td>시장 패닉, 역사적 급락 수준</td></tr>
            </tbody>
          </table>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">그래프 보는 방법</h4>
          <ul className="mi-info-ul">
            <li><strong>그래프가 급격히 올라갈 때</strong><br />→ 시장 공포 급증 신호, 주가 급락 가능<br />→ 단기적으로 투매가 나올 수 있으므로 신중한 판단 필요</li>
            <li><strong>그래프가 천천히 내려올 때</strong><br />→ 시장 불안이 서서히 해소되는 과정<br />→ 급하게 매수하기보다 추세 확인 후 진입 권장</li>
            <li><strong>15 이하로 오랫동안 유지될 때</strong><br />→ 시장이 지나치게 낙관적인 과열 신호일 수 있음</li>
            <li><strong>30 이상에서 갑자기 하락할 때</strong><br />→ 공포 심리 해소, 시장 반등 신호 — 역사적 매수 기회 다수</li>
            <li>배경색: 초록(안정) / 노랑(보통) / 주황(주의) / 빨강(경계)</li>
          </ul>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">역사적 주요 사례</h4>
          <ul className="mi-info-ul">
            <li>2008년 금융위기: 80 이상 (역대 최고)</li>
            <li>2020년 코로나 폭락: 85 이상</li>
            <li>2022년 금리인상 충격: 35 내외</li>
            <li>평상시 평균: 15 ~ 20 수준</li>
          </ul>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">활용 팁</h4>
          <ul className="mi-info-ul">
            <li>✅ VIX 급등 시 → 공포에 매수 전략 고려 (추세 확인 필수)</li>
            <li>✅ VIX 낮을 때 → 리스크 관리 강화, 과열 주의</li>
            <li>✅ S&amp;P500 그래프와 함께 비교하면 더욱 효과적</li>
          </ul>
        </section>
      </div>
    ),
  },
  dxy: {
    title: '달러인덱스(DXY)란?',
    body: (
      <div className="mi-info-body">
        <section className="mi-info-section">
          <h4 className="mi-info-h4">달러인덱스란?</h4>
          <p>달러인덱스(DXY)는 미국 달러의 가치를 6개 주요 통화 대비 상대적으로 나타낸 지수입니다. 1973년을 기준(100)으로 산출하며, 달러 강세/약세를 한눈에 파악할 수 있는 핵심 지표입니다.</p>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">구성 통화 비중</h4>
          <table className="mi-info-table">
            <thead><tr><th>통화</th><th>국가</th><th>비중</th></tr></thead>
            <tbody>
              <tr><td>유로</td><td>유럽연합</td><td>57.6%</td></tr>
              <tr><td>엔화</td><td>일본</td><td>13.6%</td></tr>
              <tr><td>파운드</td><td>영국</td><td>11.9%</td></tr>
              <tr><td>캐나다달러</td><td>캐나다</td><td>9.1%</td></tr>
              <tr><td>스웨덴크로나</td><td>스웨덴</td><td>4.2%</td></tr>
              <tr><td>스위스프랑</td><td>스위스</td><td>3.6%</td></tr>
            </tbody>
          </table>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">수치 해석 방법</h4>
          <table className="mi-info-table">
            <thead><tr><th>수치</th><th>달러 상태</th><th>의미</th></tr></thead>
            <tbody>
              <tr><td>80 이하</td><td>🔵 달러 약세</td><td>신흥국 유리, 원자재 가격 상승</td></tr>
              <tr><td>80 ~ 95</td><td>🟢 보통</td><td>안정적인 달러 수준</td></tr>
              <tr><td>95 ~ 105</td><td>🟡 강세</td><td>달러 강세, 신흥국 부담</td></tr>
              <tr><td>105 ~ 115</td><td>🟠 강한 강세</td><td>글로벌 자금 미국 집중</td></tr>
              <tr><td>115 이상</td><td>🔴 극강세</td><td>신흥국 위기 가능성, 원자재 급락</td></tr>
            </tbody>
          </table>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">그래프 보는 방법</h4>
          <ul className="mi-info-ul">
            <li><strong>달러 강세 시</strong><br />→ 원달러 환율 상승, 금·원자재 가격 하락 경향<br />→ 신흥국 증시 하락 압력, 외국인 자금 이탈</li>
            <li><strong>달러 약세 시</strong><br />→ 원달러 환율 하락, 금·원자재 가격 상승 경향<br />→ 신흥국 증시 상승 압력, 외국인 자금 유입</li>
            <li><strong>달러인덱스와 금</strong>: 대체로 역상관관계</li>
            <li><strong>달러인덱스와 원자재</strong>: 달러로 거래되므로 역상관관계</li>
            <li><strong>달러인덱스와 코스피</strong>: 달러 강세 시 외국인 매도 경향</li>
          </ul>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">역사적 주요 사례</h4>
          <ul className="mi-info-ul">
            <li>2001년 닷컴버블 붕괴 후: 120 → 70대로 급락</li>
            <li>2008년 금융위기: 안전자산 선호로 급등</li>
            <li>2022년 금리인상: 114 이상, 20년 만의 최고치</li>
            <li>평상시 평균: 90 ~ 105 수준</li>
          </ul>
        </section>
        <section className="mi-info-section">
          <h4 className="mi-info-h4">활용 팁</h4>
          <ul className="mi-info-ul">
            <li>✅ 달러 강세 시 → 국내 주식 외국인 매도 주의</li>
            <li>✅ 달러 약세 시 → 금·원자재·신흥국 주식 수혜 예상</li>
            <li>✅ 원달러 환율·VIX와 함께 활용 권장</li>
          </ul>
        </section>
      </div>
    ),
  },
};

// ── ? 아이콘 버튼 ─────────────────────────────────────────────
const InfoButton = ({ infoKey, onOpen }) => (
  <button
    className="mi-info-btn"
    onClick={e => { e.stopPropagation(); onOpen(infoKey); }}
    title="설명 보기"
  >?</button>
);

// ── 설명 모달 ─────────────────────────────────────────────────
const InfoModal = ({ infoKey, onClose }) => {
  useEffect(() => {
    if (!infoKey) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [infoKey, onClose]);

  if (!infoKey) return null;
  const content = INFO_CONTENT[infoKey];
  if (!content) return null;

  return (
    <div className="mi-modal-overlay" onClick={onClose}>
      <div className="mi-modal" onClick={e => e.stopPropagation()}>
        <div className="mi-modal-hd">
          <span className="mi-modal-title">{content.title}</span>
          <button className="mi-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="mi-modal-bd">{content.body}</div>
      </div>
    </div>
  );
};

// ── 단일 축 커스텀 툴팁 ───────────────────────────────────────
const CustomTooltip = ({ active, payload, label, fieldKey }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const diff = d[`${fieldKey}_diff`];
  const rate = d[`${fieldKey}_rate`];
  return (
    <div className="mi-tooltip">
      <div className="mi-tt-date">{label}</div>
      <div className="mi-tt-val">{fNum(d[fieldKey])}</div>
      {diff != null && (
        <div className="mi-tt-diff" style={{ color: diffColor(diff) }}>
          {fDiff(diff)} ({fRate(rate)})
        </div>
      )}
    </div>
  );
};

// ── 2축 커스텀 툴팁 ──────────────────────────────────────────
const DualTooltip = ({ active, payload, label, leftKey, rightKey, leftLabel, rightLabel, leftColor, rightColor }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="mi-tooltip">
      <div className="mi-tt-date">{label}</div>
      <div style={{ color: leftColor, fontWeight: 600, fontSize: 12, marginTop: 2 }}>
        {leftLabel}: {fNum(d[leftKey])}
      </div>
      <div style={{ color: rightColor, fontWeight: 600, fontSize: 12, marginTop: 1 }}>
        {rightLabel}: {fNum(d[rightKey])}
      </div>
    </div>
  );
};

// ── 단일 축 차트 패널 ─────────────────────────────────────────
const ChartPanel = ({ title, data, fieldKey, infoKey = null, onInfoClick, onCollect, collectLoading, colorFn, height = 180 }) => {
  const chartData  = [...data].reverse();
  const color      = colorFn ? colorFn(chartData, fieldKey) : getLineColor(chartData, fieldKey);
  const latest     = data[0];
  const latestVal  = latest ? latest[fieldKey]           : null;
  const latestDiff = latest ? latest[`${fieldKey}_diff`] : null;
  const latestRate = latest ? latest[`${fieldKey}_rate`] : null;
  const isVix    = fieldKey === 'vix';
  const isVkospi = fieldKey === 'vkospi';
  const vkSt     = isVkospi ? vkospiStatus(latestVal) : null;

  return (
    <div className="mi-chart-panel">
      <div className="mi-chart-header">
        <span className="mi-chart-title">
          {title}
          {infoKey && onInfoClick && <InfoButton infoKey={infoKey} onOpen={onInfoClick} />}
          {onCollect && (
            <button className="mi-collect-btn" onClick={onCollect} disabled={collectLoading}>
              {collectLoading ? '수집 중...' : '과거 데이터 수집'}
            </button>
          )}
        </span>
        {latestVal != null && (
          <span className="mi-chart-latest">
            <span className="mi-latest-val">{fNum(latestVal)}</span>
            {vkSt && (
              <span className="mi-vkospi-status" style={{ color: vkSt.color }}>
                &nbsp;{vkSt.emoji} {vkSt.text}
              </span>
            )}
            {latestDiff != null && (
              <span className="mi-latest-diff" style={{ color: diffColor(latestDiff) }}>
                &nbsp;{fDiff(latestDiff)} ({fRate(latestRate)})
              </span>
            )}
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          {isVix && (
            <>
              <ReferenceArea y1={0}  y2={15}  fill="rgba(76,175,80,0.10)"  ifOverflow="hidden" />
              <ReferenceArea y1={15} y2={20}  fill="rgba(255,193,7,0.13)"  ifOverflow="hidden" />
              <ReferenceArea y1={20} y2={30}  fill="rgba(255,152,0,0.12)"  ifOverflow="hidden" />
              <ReferenceArea y1={30} y2={200} fill="rgba(244,67,54,0.10)"  ifOverflow="hidden" />
              <ReferenceLine y={15} stroke="#66bb6a" strokeDasharray="4 3" strokeWidth={1} />
              <ReferenceLine y={20} stroke="#ffa726" strokeDasharray="4 3" strokeWidth={1} />
              <ReferenceLine y={30} stroke="#ef5350" strokeDasharray="4 3" strokeWidth={1} />
            </>
          )}
          {isVkospi && (
            <>
              <ReferenceArea y1={0}  y2={20}  fill="rgba(76,175,80,0.10)"  ifOverflow="hidden" />
              <ReferenceArea y1={20} y2={30}  fill="rgba(255,193,7,0.13)"  ifOverflow="hidden" />
              <ReferenceArea y1={30} y2={40}  fill="rgba(244,67,54,0.10)"  ifOverflow="hidden" />
              <ReferenceArea y1={40} y2={200} fill="rgba(156,39,176,0.08)" ifOverflow="hidden" />
              <ReferenceLine y={20} stroke="#66bb6a" strokeDasharray="4 3" strokeWidth={1} />
              <ReferenceLine y={30} stroke="#ffa726" strokeDasharray="4 3" strokeWidth={1} />
              <ReferenceLine y={40} stroke="#ef5350" strokeDasharray="4 3" strokeWidth={1} />
            </>
          )}
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="index_date" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={d => d.slice(5)} minTickGap={30} />
          <YAxis tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => Number(v).toLocaleString('ko-KR')} width={72} domain={['auto', 'auto']} />
          <Tooltip content={<CustomTooltip fieldKey={fieldKey} />} />
          <Line type="monotone" dataKey={fieldKey} stroke={color} strokeWidth={1.8} dot={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── 2축 차트 패널 ─────────────────────────────────────────────
const DualChart = ({ title, desc, data, leftKey, leftLabel, leftColor, rightKey, rightLabel, rightColor, leftInfoKey, rightInfoKey, onInfoClick }) => {
  const chartData = [...data].reverse();
  return (
    <div className="mi-chart-panel">
      <div className="mi-chart-header mi-chart-header--dual">
        <div className="mi-dual-left">
          <div className="mi-chart-title">
            {title}
            {leftInfoKey  && onInfoClick && <InfoButton infoKey={leftInfoKey}  onOpen={onInfoClick} />}
            {rightInfoKey && onInfoClick && <InfoButton infoKey={rightInfoKey} onOpen={onInfoClick} />}
          </div>
          {desc && <div className="mi-chart-desc">{desc}</div>}
        </div>
        <div className="mi-dual-legend">
          <span style={{ color: leftColor  }}>─ {leftLabel}</span>
          <span style={{ color: rightColor }}>─ {rightLabel}</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <ComposedChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="index_date" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={d => d.slice(5)} minTickGap={30} />
          <YAxis yAxisId="left"  tick={{ fontSize: 10, fill: leftColor  }} tickFormatter={v => Number(v).toLocaleString('ko-KR')} width={64} domain={['auto', 'auto']} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: rightColor }} tickFormatter={v => Number(v).toLocaleString('ko-KR')} width={52} domain={['auto', 'auto']} />
          <Tooltip content={<DualTooltip leftKey={leftKey} rightKey={rightKey} leftLabel={leftLabel} rightLabel={rightLabel} leftColor={leftColor} rightColor={rightColor} />} />
          <Line yAxisId="left"  type="monotone" dataKey={leftKey}  stroke={leftColor}  strokeWidth={1.8} dot={false} connectNulls />
          <Line yAxisId="right" type="monotone" dataKey={rightKey} stroke={rightColor} strokeWidth={1.8} dot={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ── 차트 섹션 (2×2 그리드) ───────────────────────────────────
const ChartsSection = ({ tabId, data, onInfoClick, onCollect, collectLoading }) => {
  if (tabId === 'domestic') {
    return (
      <div className="mi-charts-2x2">
        <ChartPanel
          title="코스피"
          data={data}
          fieldKey="kospi"
          onInfoClick={onInfoClick}
          colorFn={() => '#FF4444'}
        />
        <ChartPanel title="코스닥" data={data} fieldKey="kosdaq" onInfoClick={onInfoClick} />
        <ChartPanel
          title="VKOSPI (한국 공포지수)"
          data={data}
          fieldKey="vkospi"
          infoKey="vkospi"
          onInfoClick={onInfoClick}
          onCollect={onCollect}
          collectLoading={collectLoading}
          colorFn={(chartData, key) => {
            const vals = chartData.filter(d => d[key] != null);
            return getVkospiColor(vals.length ? vals[vals.length - 1][key] : null);
          }}
        />
        <ChartPanel title="코스피200" data={data} fieldKey="kospi200" onInfoClick={onInfoClick} />
      </div>
    );
  }
  return (
    <div className="mi-charts-2x2">
      {TAB_CHART_FIELDS[tabId].map(f => (
        <ChartPanel
          key={f.key}
          title={f.label}
          data={data}
          fieldKey={f.key}
          infoKey={INFO_FIELD_KEYS.has(f.key) ? f.key : null}
          onInfoClick={onInfoClick}
        />
      ))}
    </div>
  );
};

// ── 데이터 그리드 ─────────────────────────────────────────────
const DataGrid = ({ tabId, data, onInfoClick }) => {
  const fields = TAB_GRID_FIELDS[tabId];
  if (!data.length) return null;
  return (
    <div className="mi-grid-wrap">
      <table className="mi-grid">
        <thead>
          <tr>
            <th rowSpan={2} className="mi-th-date">날짜</th>
            {fields.map(f => (
              <th key={f.key} colSpan={2} className="mi-th-group">
                {f.label}
                {INFO_FIELD_KEYS.has(f.key) && onInfoClick && (
                  <InfoButton infoKey={f.key} onOpen={onInfoClick} />
                )}
              </th>
            ))}
          </tr>
          <tr>
            {fields.map(f => (
              <React.Fragment key={f.key}>
                <th className="mi-th-val">시세</th>
                <th className="mi-th-diff">등락</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr key={row.index_date}>
              <td className="mi-td-date">{row.index_date}</td>
              {fields.map(f => {
                const val  = row[f.key];
                const diff = row[`${f.key}_diff`];
                const rate = row[`${f.key}_rate`];
                return (
                  <React.Fragment key={f.key}>
                    <td className="mi-td-val">{fNum(val)}</td>
                    <td className="mi-td-diff" style={{ color: diffColor(diff) }}>
                      {diff != null ? `${fDiff(diff)} (${fRate(rate)})` : '-'}
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── 상관관계 그리드 ───────────────────────────────────────────
const CorrelationGrid = ({ data }) => {
  if (!data.length) return null;
  return (
    <div className="mi-grid-wrap">
      <table className="mi-grid">
        <thead>
          <tr>
            <th className="mi-th-date">날짜</th>
            {CORR_GRID_FIELDS.map(f => (
              <th key={f.key} className="mi-th-corr">{f.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr key={row.index_date}>
              <td className="mi-td-date">{row.index_date}</td>
              {CORR_GRID_FIELDS.map(f => {
                const val  = row[f.key];
                const diff = row[`${f.key}_diff`];
                const rate = row[`${f.key}_rate`];
                return (
                  <td key={f.key} className="mi-td-corr">
                    <div className="mi-corr-val" style={{ color: diffColor(diff) }}>{fNum(val)}</div>
                    {diff != null && (
                      <div className="mi-corr-diff" style={{ color: diffColor(diff) }}>
                        {fDiff(diff)} ({fRate(rate)})
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── 국내 KOSPI-VKOSPI 상관관계 차트 (국내지수 탭 좌상단) ─────
const DomesticCorrChart = ({ data, onInfoClick }) => {
  const pairs = [...data].filter(d => d.kospi != null && d.vkospi != null).reverse();

  if (pairs.length === 0) {
    return (
      <div className="mi-chart-panel">
        <div className="mi-chart-header mi-chart-header--dual">
          <div className="mi-dual-left">
            <div className="mi-chart-title">
              코스피 / VKOSPI 상관관계
              <InfoButton infoKey="vkospi" onOpen={onInfoClick} />
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, padding: '60px 0' }}>
          VKOSPI 데이터 수집 중
        </div>
      </div>
    );
  }

  const xs      = pairs.map(d => parseFloat(d.kospi));
  const ys      = pairs.map(d => parseFloat(d.vkospi));
  const r       = calcCorrelation(xs, ys);
  const hasCorr = !isNaN(r);
  const latest  = pairs[pairs.length - 1];

  return (
    <div className="mi-chart-panel">
      <div className="mi-chart-header mi-chart-header--dual">
        <div className="mi-dual-left">
          <div className="mi-chart-title">
            코스피 / VKOSPI 상관관계
            <InfoButton infoKey="vkospi" onOpen={onInfoClick} />
          </div>
          {hasCorr && (
            <div className="mi-chart-desc">
              상관계수 {r.toFixed(2)} · {corrDescText(r)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0, marginLeft: 8 }}>
          {latest?.kospi != null && (
            <span style={{ color: '#FF4444', fontSize: 11, fontWeight: 700 }}>
              코스피: {fNum(latest.kospi)}
            </span>
          )}
          {latest?.vkospi != null && (
            <span style={{ color: '#4444FF', fontSize: 11, fontWeight: 700 }}>
              VKOSPI: {Number(latest.vkospi).toFixed(2)}
            </span>
          )}
          <div className="mi-dual-legend" style={{ marginTop: 2 }}>
            <span style={{ color: '#FF4444' }}>─ 코스피</span>
            <span style={{ color: '#4444FF' }}>─ VKOSPI</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={148}>
        <ComposedChart data={pairs} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="index_date" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={d => d.slice(5)} minTickGap={30} />
          <YAxis yAxisId="kospi"  orientation="left"  tick={{ fontSize: 10, fill: '#FF4444' }} tickFormatter={v => Number(v).toLocaleString('ko-KR')} width={68} domain={['auto', 'auto']} />
          <YAxis yAxisId="vkospi" orientation="right" tick={{ fontSize: 10, fill: '#4444FF' }} tickFormatter={v => parseFloat(v).toFixed(1)} width={44} domain={['auto', 'auto']} />
          <Tooltip content={<DualTooltip leftKey="kospi" rightKey="vkospi" leftLabel="코스피" rightLabel="VKOSPI" leftColor="#FF4444" rightColor="#4444FF" />} />
          <Line yAxisId="kospi"  type="monotone" dataKey="kospi"  stroke="#FF4444" strokeWidth={2} dot={false} connectNulls />
          <Line yAxisId="vkospi" type="monotone" dataKey="vkospi" stroke="#4444FF" strokeWidth={2} dot={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
      <div style={{ fontSize: 10, color: '#999', marginTop: 6, lineHeight: 1.5 }}>
        VKOSPI는 코스피의 변동성 지수입니다. 일반적으로 코스피 하락 시 VKOSPI 상승, 코스피 상승 시 VKOSPI 하락하는 역상관 관계를 보입니다.
      </div>
    </div>
  );
};

// ── 상관관계 탭 섹션 ─────────────────────────────────────────
const CorrelationSection = ({ data, onInfoClick }) => (
  <>
    <div className="mi-charts-2x2">
      {CORR_CHARTS.map(cfg => (
        <DualChart key={cfg.id} data={data} onInfoClick={onInfoClick} {...cfg} />
      ))}
    </div>
    <CorrelationGrid data={data} />
  </>
);

// ── 2개종목비교 탭 ─────────────────────────────────────────────
// 정규화/차트 렌더링 로직은 건별수익률현황의 비교 팝업(StockCompareModal)과 공유 (CompareChart.js)
const COMPARE_CHARTS = [
  { id: 'sp500',     title: 'TIGER 미국S&P500(360750) vs SPY',
    domesticTicker: '360750', domesticLabel: 'TIGER 미국S&P500',
    compareTicker: 'SPY',     compareLabel: 'SPY',         useGold: false },
  { id: 'nasdaq100', title: 'KODEX 미국나스닥100(379810) vs QQQ',
    domesticTicker: '379810', domesticLabel: 'KODEX 미국나스닥100',
    compareTicker: 'QQQ',     compareLabel: 'QQQ',         useGold: false },
  { id: 'dividend',  title: 'TIGER 미국배당다우존스(458730) vs SCHD',
    domesticTicker: '458730', domesticLabel: 'TIGER 미국배당다우존스',
    compareTicker: 'SCHD',    compareLabel: 'SCHD',        useGold: false },
  { id: 'bond',      title: 'TIGER 미국달러단기채권액티브(329750) vs SHY',
    domesticTicker: '329750', domesticLabel: 'TIGER 미국달러단기채권액티브',
    compareTicker: 'SHY',     compareLabel: 'SHY',         useGold: false },
  { id: 'gold',      title: 'ACE KRX금현물(411060) vs 국제 금시세',
    domesticTicker: '411060', domesticLabel: 'ACE KRX금현물',
    compareTicker: null,      compareLabel: '국제 금시세', useGold: true },
];

// multi-history로 한 번에 조회할 티커 목록 (금현물은 tb_daily_price, 국제 금시세는 tb_market_index.gold)
const COMPARE_TICKERS = COMPARE_CHARTS
  .flatMap(c => [c.domesticTicker, c.compareTicker])
  .filter(Boolean);


// ── 2개종목비교 탭 섹션 (6개 그래프 세로 배치, 데이터 그리드 없음) ──
const CompareSection = ({ marketData, compareData, loading }) => {
  if (loading) return <div className="mi-loading">조회 중...</div>;

  const fxNorm   = normalizeSeries(marketFieldToSeries(marketData, 'usd_krw'));
  const goldNorm = normalizeSeries(marketFieldToSeries(marketData, 'gold'));

  return (
    <div className="mi-compare-stack">
      <ChartPanel
        title="원달러환율(USD/KRW)"
        data={marketData}
        fieldKey="usd_krw"
        colorFn={() => COMPARE_COLORS.fx}
        height={260}
      />
      {COMPARE_CHARTS.map(cfg => {
        const domesticNorm = normalizeSeries(compareData[cfg.domesticTicker] || []);
        const compareNorm  = cfg.useGold ? goldNorm : normalizeSeries(compareData[cfg.compareTicker] || []);
        const merged = mergeNormalized({ domestic: domesticNorm, compare: compareNorm, fx: fxNorm });
        const series = [
          { key: 'domestic', label: cfg.domesticLabel, color: COMPARE_COLORS.domestic },
          { key: 'compare',  label: cfg.compareLabel,  color: COMPARE_COLORS.compare  },
          // 환율은 변동폭이 작아 국내ETF/해외종목과 같은 축이면 평탄하게 보이므로 보조축(우측) 사용
          { key: 'fx',       label: '원달러환율',       color: COMPARE_COLORS.fx, axis: 'right' },
        ];
        return (
          <CompareLineChart key={cfg.id} title={cfg.title} mergedData={merged} series={series} height={260} />
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ════════════════════════════════════════════════════════════
const MarketIndexPage = ({ isActive }) => {
  const [periodIdx,   setPeriodIdx]   = useState(1);
  const [customStart, setCustomStart] = useState('');
  const [customEnd,   setCustomEnd]   = useState(toYMD(new Date()));
  const [dateRange,   setDateRange]   = useState(calcDateRange(90));
  const [activeTab,   setActiveTab]   = useState('domestic');
  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [message,     setMessage]     = useState({ text: '', type: '' });
  const [infoKey,          setInfoKey]          = useState(null);
  const [vkospiCollecting, setVkospiCollecting] = useState(false);
  const [compareData,    setCompareData]    = useState({});
  const [compareLoading, setCompareLoading] = useState(false);

  const showMsg = (text, type = 'info', ms = 4000) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), ms);
  };

  useEffect(() => {
    const p = PERIODS[periodIdx];
    if (p.days !== null) setDateRange(calcDateRange(p.days));
  }, [periodIdx]);

  useEffect(() => {
    if (!isActive || !dateRange.start || !dateRange.end) return;
    let cancelled = false;
    setLoading(true);
    fetchMarketIndex(dateRange.start, dateRange.end)
      .then(res => { if (!cancelled) setData(res.data || []); })
      .catch(e  => { if (!cancelled) showMsg('조회 실패: ' + (e.response?.data?.detail || e.message), 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isActive, dateRange]);

  // '2개종목비교' 탭에서만 다종목 시세 이력 조회 (다른 탭 전환 시 불필요한 호출 방지)
  useEffect(() => {
    if (!isActive || activeTab !== 'compare' || !dateRange.start || !dateRange.end) return;
    let cancelled = false;
    setCompareLoading(true);
    fetchMultiPriceHistory(COMPARE_TICKERS, dateRange.start, dateRange.end)
      .then(res => { if (!cancelled) setCompareData(res.data || {}); })
      .catch(e  => { if (!cancelled) showMsg('종목비교 조회 실패: ' + (e.response?.data?.detail || e.message), 'error'); })
      .finally(() => { if (!cancelled) setCompareLoading(false); });
    return () => { cancelled = true; };
  }, [isActive, activeTab, dateRange]);

  const handleCollectVkospi = () => {
    setVkospiCollecting(true);
    collectVkospi()
      .then(res => {
        if (res.success === false) {
          showMsg(res.message || 'VKOSPI 자동 수집 불가 — KRX 전용 지수로 무료 API 미지원', 'warn', 10000);
          return null;
        }
        showMsg(`VKOSPI 수집 완료: ${res.collected}건 수집, ${res.updated}건 업데이트`, 'info');
        return fetchMarketIndex(dateRange.start, dateRange.end);
      })
      .then(res => { if (res) setData(res.data || []); })
      .catch(e => showMsg('VKOSPI 수집 오류: ' + (e.response?.data?.detail || e.message), 'error'))
      .finally(() => setVkospiCollecting(false));
  };

  const handleCustomSearch = () => {
    if (!customStart || !customEnd) { showMsg('시작일과 종료일을 모두 입력하세요.', 'warn'); return; }
    setDateRange({ start: customStart, end: customEnd });
  };

  const toolbar = (
    <div className="mi-toolbar">
      {PERIODS.map((p, i) => (
        <label key={p.label} className="mi-period-label">
          <input type="radio" name="mi-period" checked={periodIdx === i} onChange={() => setPeriodIdx(i)} />
          {p.label}
        </label>
      ))}
      {PERIODS[periodIdx].days === null && (
        <span className="mi-custom-range">
          <input type="date" className="mi-date-input" value={customStart} onChange={e => setCustomStart(e.target.value)} />
          <span>~</span>
          <input type="date" className="mi-date-input" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
          <button className="mi-btn primary" onClick={handleCustomSearch}>조회</button>
        </span>
      )}
    </div>
  );

  return (
    <PageLayout title="시장지수현황" toolbar={toolbar} message={message} scrollable>
      <InfoModal infoKey={infoKey} onClose={() => setInfoKey(null)} />

      <div className="mi-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`mi-tab-btn${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mi-loading">조회 중...</div>
      ) : data.length === 0 ? (
        <div className="mi-no-data">
          데이터 없음 — 관리자 &gt; 시세수집에서 시장지수를 수집하세요.
        </div>
      ) : activeTab === 'compare' ? (
        <CompareSection marketData={data} compareData={compareData} loading={compareLoading} />
      ) : activeTab === 'correlation' ? (
        <CorrelationSection data={data} onInfoClick={setInfoKey} />
      ) : activeTab === 'domestic' ? (
        <>
          <ChartsSection tabId="domestic" data={data} onInfoClick={setInfoKey} onCollect={handleCollectVkospi} collectLoading={vkospiCollecting} />
          <DataGrid      tabId="domestic" data={data} onInfoClick={setInfoKey} />
        </>
      ) : (
        <>
          <ChartsSection tabId={activeTab} data={data} onInfoClick={setInfoKey} />
          <DataGrid      tabId={activeTab} data={data} onInfoClick={setInfoKey} />
        </>
      )}
    </PageLayout>
  );
};

export default MarketIndexPage;
